import { AccountType } from '@/data/usecases/account/account-type'
import { LabelValueOptionModel } from '@/domain/models/onboarding/label-value-option-model'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { AccountProfessional } from '@/domain/usecases/account/account-professional'
import { OnboardingOptions } from '@/domain/usecases/onboarding/onboarding-options'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { makeSkillWebMyProfileRedirectUrl } from '@/main/factories/http'
import BaseLoader from '@/main/routes/loaders/base-loader'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'

export type UserProfileLoaderBenefitOptionsType = {
  accountBenefits: AccountBenefit.Model
  benefitsOptions?: AccountBenefit.OptionsModel
}

export type UserProfileLoaderProfessionalOptionsType = {
  areas: LabelValueOptionModel<number>[]
  seniorities: LabelValueOptionModel<number>[]
}

export type UserProfileLoaderDataType = {
  accountProfile: Account.ProfileModel
  accountProfessional?: AccountProfessional.Model
  accountBenefits: AccountBenefit.Model
  professionalOptions?: UserProfileLoaderProfessionalOptionsType
  benefitsOptions: AccountBenefit.OptionsModel
}

export default class UserProfileLoader extends BaseLoader {
  constructor(
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined,
    readonly account: Account.Model
  ) {
    super(
      account,
      makeSkillWebMyProfileRedirectUrl('profile'),
      queryClient,
      AccountType.Enum.DEPENDENT.is(account)
        ? FeatureFlag.Key.DEPENDENT_USER_PROFILE
        : FeatureFlag.Key.COMPANIES_ON_USER_PROFILE,
      ldClient
    )
  }

  private async getAccountProfile(): Promise<Account.ProfileModel> {
    return super.importRemoteModule('makeRemoteAccountProfile').then(async remote => {
      return (remote(this.queryClient) as Account).loader()
    })
  }

  private async getProfessionalOptions(): Promise<
    UserProfileLoaderProfessionalOptionsType | undefined
  > {
    if (AccountType.Enum.DEPENDENT.is(this.account)) return undefined
    return super
      .importRemoteModule('makeRemoteOnboardingOptions')
      .then(async makeRemoteOnboardingOptions => {
        const remoteOnboardingOptions = makeRemoteOnboardingOptions(
          this.queryClient
        ) as OnboardingOptions
        return remoteOnboardingOptions.getStep1()
      })
      .then(data => {
        return {
          areas: data.professionalAreas,
          seniorities: data.professionalSeniorities
        }
      })
  }

  private async getAccountProfessional(): Promise<AccountProfessional.Model | undefined> {
    if (AccountType.Enum.DEPENDENT.is(this.account)) return undefined
    return super.importRemoteModule('makeRemoteAccountProfessional').then(async remote => {
      return (remote(this.queryClient) as AccountProfessional).get(this.account.id)
    })
  }

  private async getAccountBenefits(): Promise<UserProfileLoaderBenefitOptionsType> {
    return super.importRemoteModule('makeRemoteAccountProfile').then(async remote => {
      const remoteBenefits = remote(this.queryClient) as Account
      return remoteBenefits.getBenefits(this.account.id).then(benefits => ({
        accountBenefits: benefits,
        benefitsOptions: {
          passPlans: benefits.contractedPlan.plan ? [benefits.contractedPlan.plan] : []
        }
      }))
    })
  }

  async loader() {
    if (
      (AccountType.Enum.DEPENDENT.is(this.account) &&
        !FeatureFlag.Key.DEPENDENT_USER_PROFILE.getValue(this.ldClient)) ||
      (AccountType.Enum.COLLABORATOR.is(this.account) &&
        !FeatureFlag.Key.COMPANIES_ON_USER_PROFILE.getValue(this.ldClient))
    ) {
      return super.notAllowedLoader()
    }

    return this.deferExec({
      data: Promise.all([
        this.getAccountProfile(),
        this.getAccountProfessional(),
        this.getAccountBenefits(),
        this.getProfessionalOptions()
      ]).then(
        ([
          accountProfile,
          accountProfessional,
          { accountBenefits, benefitsOptions },
          professionalOptions
        ]) => ({
          accountProfile,
          accountProfessional,
          accountBenefits,
          benefitsOptions,
          professionalOptions
        })
      )
    })
  }
}
